import { Download } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import type { ReportCourse } from '@/hooks/queries/useReportCourses';
import { useTabLabels } from '@/hooks/useTabLabels';
import { cn } from '@/lib/utils';

import CourseLink from './CourseLink';
import ReportTable, { type ReportColumn } from './ReportTable';
import {
	buildReadinessRows,
	downloadCsv,
	filterReadinessRows,
	type ReadinessGapType,
	reportCsvFilename,
	summarizeReadiness,
	yesNo,
} from './reportUtils';

type ReadinessReportProps = {
	courses: ReportCourse[];
	unmappedCourseIds: Set<string>;
	schoolId: string;
};

const GapCell = ({ value }: { value: boolean }) =>
	value ? (
		<span className="font-medium text-amber-700">Yes</span>
	) : (
		<span className="text-(--header-text)">No</span>
	);

const ReadinessReport = ({ courses, unmappedCourseIds, schoolId }: ReadinessReportProps) => {
	const labels = useTabLabels();
	const [gapsOnly, setGapsOnly] = useState(true);
	const [selectedGaps, setSelectedGaps] = useState<ReadinessGapType[]>([]);

	const allRows = useMemo(
		() => buildReadinessRows(courses, unmappedCourseIds),
		[courses, unmappedCourseIds]
	);
	const summary = summarizeReadiness(allRows);

	const rows = useMemo(
		() =>
			filterReadinessRows(allRows, {
				gapsOnly,
				gapTypes: selectedGaps,
			}),
		[allRows, gapsOnly, selectedGaps]
	);

	const columns: ReportColumn[] = useMemo(
		() => [
			{ id: 'grade', label: 'Grade', sortType: 'grade' },
			{ id: 'discipline', label: 'Discipline' },
			{ id: 'course', label: 'Course' },
			{
				id: 'missingDescription',
				label: `Missing ${labels.description}`,
				sortType: 'boolean',
			},
			{ id: 'noOutcomes', label: `No ${labels.outcomes}`, sortType: 'boolean' },
			{ id: 'noResources', label: `No ${labels.resources}`, sortType: 'boolean' },
			{ id: 'noUnits', label: `No ${labels.units}`, sortType: 'boolean' },
			{ id: 'noOwner', label: 'No Owner', sortType: 'boolean' },
			{ id: 'notOnMap', label: 'Not On Map', sortType: 'boolean' },
			{ id: 'draft', label: 'Draft', sortType: 'boolean' },
			{ id: 'gapCount', label: 'Gaps', sortType: 'number' },
		],
		[labels.description, labels.outcomes, labels.resources, labels.units]
	);

	const gapTypeChips: Array<{ key: ReadinessGapType; label: string }> = useMemo(
		() => [
			{ key: 'missingDescription', label: `No ${labels.description.toLowerCase()}` },
			{ key: 'noOutcomes', label: `No ${labels.outcomes.toLowerCase()}` },
			{ key: 'noResources', label: `No ${labels.resources.toLowerCase()}` },
			{ key: 'noUnits', label: `No ${labels.units.toLowerCase()}` },
			{ key: 'noOwner', label: 'No owner' },
			{ key: 'notOnMap', label: 'Not on map' },
			{ key: 'draft', label: 'Draft' },
		],
		[labels.description, labels.outcomes, labels.resources, labels.units]
	);

	const handleDownload = () => {
		downloadCsv(
			reportCsvFilename('readiness', schoolId),
			[
				'Grade',
				'Discipline',
				'Course',
				`Missing ${labels.description}`,
				`No ${labels.outcomes}`,
				`No ${labels.resources}`,
				`No ${labels.units}`,
				'No Owner',
				'Not On Map',
				'Draft',
				'Gap Count',
			],
			rows.map((row) => [
				row.grade,
				row.discipline,
				row.course,
				yesNo(row.missingDescription),
				yesNo(row.noOutcomes),
				yesNo(row.noResources),
				yesNo(row.noUnits),
				yesNo(row.noOwner),
				yesNo(row.notOnMap),
				yesNo(row.draft),
				row.gapCount,
			])
		);
	};

	const handleGapTypeClick = (key: ReadinessGapType) => {
		setSelectedGaps((current) =>
			current.includes(key) ? current.filter((gap) => gap !== key) : [...current, key]
		);
	};

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div>
					<h2 className="text-[16px] font-bold leading-5 text-(--header-text)">
						Curriculum readiness
					</h2>
					<p className="mt-1 text-sm text-(--header-text)">
						{summary.withGaps} of {summary.total} course
						{summary.total === 1 ? '' : 's'} have gaps
						{' · '}
						showing {rows.length}
						{selectedGaps.length > 1 ? ' · matching any selected gap' : ''}
					</p>
				</div>
				<Button type="button" variant="outline" size="small" onClick={handleDownload}>
					<Download size={14} className="mr-1.5" />
					Download CSV
				</Button>
			</div>

			<label
				htmlFor="readiness-gaps-only"
				className="flex w-fit cursor-pointer items-center gap-2 text-sm text-(--body-text)"
			>
				<Checkbox
					id="readiness-gaps-only"
					checked={gapsOnly}
					onCheckedChange={(checked) => {
						setGapsOnly(checked === true);
						if (checked !== true) setSelectedGaps([]);
					}}
					aria-label="Only courses with gaps"
				/>
				Only courses with gaps
			</label>

			<fieldset
				className="m-0 flex flex-wrap gap-2 border-0 p-0"
				aria-label="Filter by gap type"
			>
				{gapTypeChips.map((chip) => {
					const isActive = selectedGaps.includes(chip.key);
					const value = summary[chip.key];
					return (
						<button
							key={chip.key}
							type="button"
							onClick={() => handleGapTypeClick(chip.key)}
							aria-pressed={isActive}
							className={cn(
								'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors',
								isActive
									? 'border-purple-900 bg-purple-900 text-white'
									: 'border-border bg-white text-(--body-text) hover:bg-purple-50'
							)}
						>
							<span>{chip.label}</span>
							<span
								className={cn('font-semibold', isActive ? 'text-white' : 'text-muted-foreground')}
							>
								{value}
							</span>
						</button>
					);
				})}
			</fieldset>

			<ReportTable
				columns={columns}
				rows={rows.map((row) => ({
					key: row.courseId,
					searchText: [row.grade, row.discipline, row.course].join(' '),
					sortValues: [
						row.grade,
						row.discipline,
						row.course,
						row.missingDescription,
						row.noOutcomes,
						row.noResources,
						row.noUnits,
						row.noOwner,
						row.notOnMap,
						row.draft,
						row.gapCount,
					],
					cells: [
						row.grade || '—',
						row.discipline || '—',
						<CourseLink key="course" courseId={row.courseId}>
							{row.course}
						</CourseLink>,
						<GapCell key="desc" value={row.missingDescription} />,
						<GapCell key="out" value={row.noOutcomes} />,
						<GapCell key="res" value={row.noResources} />,
						<GapCell key="units" value={row.noUnits} />,
						<GapCell key="owner" value={row.noOwner} />,
						<GapCell key="map" value={row.notOnMap} />,
						<GapCell key="draft" value={row.draft} />,
						row.gapCount,
					],
				}))}
				emptyMessage="No courses match this readiness queue filter."
				searchPlaceholder="Search readiness queue…"
			/>
		</div>
	);
};

export default ReadinessReport;
